import React, { useEffect, useState } from 'react';

interface GooglePickerProps {
  accessToken: string;
  clientId: string;
  onSelect?: (files: any[]) => void;
  onCancel?: () => void;
}

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

export const GooglePicker: React.FC<GooglePickerProps> = ({ accessToken, clientId, onSelect, onCancel }) => {
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load Google API
    const loadGoogleApi = () => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('picker', () => {
          setPickerApiLoaded(true);
          setLoading(false);
        });
      };
      script.onerror = () => {
        setError('Failed to load Google Picker API');
        setLoading(false);
      };
      document.body.appendChild(script);
    };

    if (!window.gapi) {
      loadGoogleApi();
    } else {
      window.gapi.load('picker', () => {
        setPickerApiLoaded(true);
        setLoading(false);
      });
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  useEffect(() => {
    if (pickerApiLoaded && accessToken) {
      createPicker();
    }
  }, [pickerApiLoaded, accessToken]);

  const createPicker = () => {
    if (!window.google) {
      setError('Google Picker API not loaded');
      return;
    }

    const picker = new window.google.picker.PickerBuilder()
      .addView(
        new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS)
          .setIncludeFolders(false)
          .setMode(window.google.picker.DocsViewMode.LIST)
      )
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(accessToken)
      .setAppId(clientId) // Important: Must set AppId to avoid 404 errors
      .setCallback((data: any) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const files = data.docs.map((doc: any) => ({
            id: doc.id,
            name: doc.name,
            mimeType: doc.mimeType,
            url: doc.url
          }));
          console.log('Selected files:', files);
          if (onSelect) {
            onSelect(files);
          }
        } else if (data.action === window.google.picker.Action.CANCEL) {
          console.log('Picker cancelled');
          if (onCancel) {
            onCancel();
          }
        }
      })
      .setTitle('Select Spreadsheets to Grant Access')
      .build();

    picker.setVisible(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Google Picker...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="text-red-500 text-center mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium text-center">{error}</p>
          <button
            onClick={onCancel}
            className="mt-4 w-full bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // The picker renders itself, so we just show a loading state while it initializes
  return null;
};